import React, { useState, useMemo } from 'react';
import { colors, Div } from 'globalStyle';
import { getContrastTextColor } from 'util/contrast';
import styled, { keyframes } from 'styled-components';
import { Box, Grid, makeStyles } from '@material-ui/core';
import ArrowDown from 'assets/icons/arrow_dropdown_black.svg';
import ArrowUp from 'assets/icons/arrow_dropdown_up_black.svg';

import { Title } from './Texts';

const useStyles = makeStyles((theme) => ({
  mainContainer: (props) => ({
    fontFamily: '"Montserrat", sans-serif',
    position: 'relative',
    borderRadius: props.isDesktop ? '20px' : '10px',
    height: props.showFullDescription ? '100%' : '',
    [theme.breakpoints.up('lg')]: {
      padding: props.padding || '20px 30px',
    },
    padding: props.padding || (props.isDesktop ? '20px' : '10px'),
    '&:hover': {
      fontSize: props.hoverDescription && '13px',
      background: props.hoverDescription && props.colorByProjectType,
      color: props.hoverDescription && `${colors.GRAY_6} !important`,
      padding: props.paddingHover || (props.paddingHover && '20px'),
      '& $description': {
        color: props.hoverDescription && `${props.descriptionColor} !important`,
      },
    },
  }),
  mainInfoContainer: (props) => ({
    marginBottom: (props.showFullDescription && (!props.isDesktop || !props.isBigTitle)) ? '12px' : '',
    marginTop: (props.description && (!props.isDesktop || !props.isBigTitle)) ? '10px' : '',
    height: 'fit-content',
  }),
  image: (props) => ({
    width: props.imageWidth || undefined,
    marginRight: props.isDesktop && `${theme.spacing(1)}px`,
  }),
  mainTextContainer: (props) => ({
    maxWidth: props.imageWidth
      ? `calc(100% - ${props.imageWidth} - ${theme.spacing(2)}px)`
      : '',
  }),
  subtitle: {
    display: 'block',
    fontSize: '12px',
    color: colors.GRAY_6,
    [theme.breakpoints.up('lg')]: {
      fontSize: '14px',
    },
  },
  title: (props) => ({
    fontWeight: 'bold !important',
    fontSize: '12px',
    color: colors.GRAY_6,
    [theme.breakpoints.up('lg')]: {
      fontSize: props.isBigTitle ? '15px !important' : '20px !important',
    },
    '& span': {
      overflow: !props.isDesktop && 'hidden',
      textAlign: !props.isDesktop && 'center',
      display: !props.isDesktop && '-webkit-box',
      '-webkit-line-clamp': !props.isDesktop && 1,
      textOverflow: !props.isDesktop && 'ellipsis',
      '-webkit-box-orient': !props.isDesktop && 'vertical',
    },
  }),
  moreInfo: {
    fontWeight: 'regular',
    fontSize: '12px',
    [theme.breakpoints.up('lg')]: {
      fontSize: '12px',
    },
  },
  description: (props) => ({
    width: '100%',
    display: 'block',
    color: colors.GRAY_1,
    textAlign: props.centerDescription ? 'center' : '',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize: !props.isDesktop && '12px',
    whiteSpace: props.showFullDescription ? '' : 'nowrap',
    height: props.showFullDescription ? '' : '20px',
    marginTop: props.showFullDescription ? '10px' : '',
  }),
  arrow: {
    position: 'absolute',
    bottom: '0px',
    left: '0px',
    padding: '15px',
    cursor: 'pointer',
  },
}));

const InfoCard = ({
  image,
  title,
  subtitle,
  isBigTitle,
  description,
  className,
  dynamicTitle,
  fullDescription,
  moreInfo,
  classes = {},
  color,
  isDesktop,
  hoverTitleText,
  hoverDescription,
  hoverDescriptionText,
  colorByProjectType,
  ...props
}) => {
  const [showFullDescription, setShowFullDescription] = useState(
    fullDescription,
  );
  const [isHovered, setIsHovered] = useState(!hoverDescription);
  const styleProps = useMemo(
    () => ({
      ...props,
      isDesktop,
      description,
      hoverDescription,
      showFullDescription,
      colorByProjectType,
      descriptionColor: getContrastTextColor(colorByProjectType),
      isBigTitle: isBigTitle || title?.length > 35,
    }),
    [showFullDescription, description, props, hoverDescription, isDesktop, isBigTitle, title],
  );
  const styles = useStyles(styleProps);
  return (
    <CardContainer
      container
      isHovered={isHovered}
      alignItems={!description ? 'center' : undefined}
      className={`${styles.mainContainer} ${className}`}
      alignContent={isDesktop ? 'center' : 'flex-start'}
      onMouseEnter={hoverDescription ? (() => setIsHovered(true)) : undefined}
      onMouseLeave={hoverDescription ? (() => setIsHovered(false)) : undefined}
      colorByProjectType={colorByProjectType}
    >
      {(!hoverDescription || !isHovered) && (
        <Grid
          container
          justify="center"
          alignItems="center"
          spacing={1}
          className={styles.mainInfoContainer}
        >
          <Grid item>
            <Box
              className={`${styles.image} ${classes.img}`}
            >
              {image}
            </Box>
          </Grid>
          <Grid item className={styles.mainTextContainer}>
            <span className={styles.subtitle}>{subtitle}</span>
            <CardTitle
              title={title}
              styles={styles}
              show={isDesktop}
              classes={classes}
              dynamicTitle={dynamicTitle}
              showFullDescription={showFullDescription}
            />
            <Div className={`${styles.moreInfo} ${classes.moreInfo}`} isCenter>
              {moreInfo}
            </Div>
          </Grid>
          {!isDesktop && (
            <Grid item container justify="center">
              <CardTitle
                show
                title={title}
                styles={styles}
                classes={classes}
                dynamicTitle={dynamicTitle}
                showFullDescription={showFullDescription}
              />
            </Grid>
          )}
        </Grid>
      )}
      {description && !hoverDescription && (
        <>
          <div
            style={{
              border: '1px solid',
              borderColor: color || '#707070',
              width: '100%',
              height: '0',
            }}
          />
          <Grid item xs={12}>
            <span className={styles.description}>{description}</span>
          </Grid>
          {!fullDescription && (
            <Grid
              container
              className={styles.arrow}
              onClick={() => setShowFullDescription((v) => !v)}
            >
              <Box
                component="img"
                src={showFullDescription ? ArrowUp : ArrowDown}
                margin="auto"
              />
            </Grid>
          )}
        </>
      )}
      {hoverDescription && isHovered && (
        <>
          <Title
            color={getContrastTextColor(colorByProjectType)}
            isCenter
            size={isDesktop && 15}
          >
            <span>
              {hoverTitleText || subtitle}
            </span>
          </Title>
          <span className={styles.description}>{hoverDescriptionText || description}</span>
        </>
      )}
    </CardContainer>
  );
};

const CardTitle = ({
  dynamicTitle, showFullDescription, show = false, styles, title, classes,
}) => {
  if (show) {
    return (
      <>
        {dynamicTitle
          ? (
            <Title
              showFullDescription={showFullDescription}
              isOverflow={!showFullDescription}
              className={`${styles.title} ${classes.title}`}
            >
              {title}
            </Title>
          )
          : <span className={`${styles.title} ${classes.title}`}>{title}</span> }
      </>
    );
  }
  return '';
};

export default InfoCard;

export const InfoCardTemplate = ({
  className, isDesktop, children, ...props
}) => {
  const styleProps = useMemo(
    () => ({
      ...props,
      isDesktop,
    }),
    [props, isDesktop],
  );
  const styles = useStyles(styleProps);
  return (
    <Grid
      container
      className={`${styles.mainContainer} ${className}`}
      alignContent={isDesktop ? 'center' : 'flex-start'}
    >
      {children}
    </Grid>
  );
};

const fadeInAnimation = keyframes`
  0% {
    opacity: 0.1;
  }
  100% {
    opacity: 1;
  }
`;
const fadeInAnimation2 = (color) => keyframes`
  0% {
    background: ${color};
  }
  100% {
    background: ${colors.WHITE};
  }
`;

const CardContainer = styled(Grid)`
  animation: ${(props) => (props.isHovered ? fadeInAnimation : fadeInAnimation2(props.colorByProjectType))} 0.3s ease-in;
`;
